/* eslint-disable react/prefer-exact-props */
import React, { useEffect } from 'react';
import { Capsule } from 'Components/frameworks/capsule/Capsule';
import { BucketFormikInput as FormikInput } from 'Src/types/forms';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Title } from 'Components/frameworks/title/Title';
import { TextSeeMore } from 'Components/frameworks/text-see-more/TextSeeMore';
import { getLegalAgreementValidator } from 'Components/widgets/contact-info/ContactInfoUtils';
import { useFormikContext } from 'formik';
import type { ContactInfoFormValues } from 'Src/types/forms';
import { t } from 'Src/i18n';
// types
type CguItemParams = {
  cguItem: CGUItemSingle;
  i18n: I18N;
};
type MultiCGUBlockProps = {
  i18n: I18N;
  idsCGU: Array<string>;
};
export type CGUItemSingle = {
  id: string;
  title: string;
  label: string;
  url: string;
  urlLabel: string;
  description: string;
  required: boolean;
};

// CGU ITEM
const CGUItem = ({ cguItem, i18n }: CguItemParams) => {
  const formik = useFormikContext<ContactInfoFormValues>();
  return (
    <div className="mb-20" data-testid="multiCGUBlock-cguItem">
      <div className="mb-10 text-20 font-bold text-saffron">{cguItem.title}</div>
      <div className="mb-10 whitespace-pre-wrap text-black">{cguItem.description}</div>
      <FormikInput
        label={i18n.booking.participants.agreeButton}
        name={`adults.0.optinsCGU.${cguItem.id}.optin`}
        type="checkbox"
        additionnalClasses="text-16"
        onChange={() => {
          formik.setFieldValue(`adults.0.optinsCGU.${cguItem.id}`, {
            channel: cguItem.id,
            optin: !formik.values.adults[0].optinsCGU?.[cguItem.id]?.optin,
            optin_partners: false,
          });
        }}
        validate={getLegalAgreementValidator()}
        isRequired={cguItem.required}
      />
    </div>
  );
};

// MULTICGUs BLOCK
export const MultiCGUBlock = ({ i18n, idsCGU }: MultiCGUBlockProps) => {
  const formik = useFormikContext<ContactInfoFormValues>();
  const CGUs = i18n.bookingProcess;
  // filter the CGU to show in the block ( omitting phone/mail optin ... )
  const desiredCGUS = CGUs?.filter((cguItem) => idsCGU.includes(cguItem.id));
  useEffect(() => {
    if (desiredCGUS.length > 0) {
      const initialOptins = desiredCGUS.reduce((acc, cguItem) => {
        if (cguItem.required) {
          acc[cguItem.id] = {
            channel: cguItem.id,
            optin: cguItem.preChecked || false,
            optin_partners: false,
          };
        }

        return acc;
      }, {});
      formik.setFieldValue('adults.0.optinsCGU', initialOptins);
    }
  }, []);
  useEffect(() => {
    const optins = formik.values.adults?.[0]?.optinsCGU || {};
    const allChecked = Object.values(optins).every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        'optin' in item &&
        (item as { optin?: boolean }).optin === true,
    );

    if (formik.values.adults[0]?.checkAll !== allChecked) {
      formik.setFieldValue('adults.0.checkAll', allChecked);
    }
  }, [formik.values.adults[0]?.optinsCGU]);

  const checkAll = (e) => {
    const isChecked = e.currentTarget.checked;
    const currentOptins = formik.values.adults?.[0]?.optinsCGU || {};
    const updatedOptins = Object.fromEntries(
      Object.entries(currentOptins).map(([key, value]) => [key, { ...value, optin: isChecked }]),
    );
    formik.setFieldValue('adults.0.optinsCGU', updatedOptins);
    formik.setFieldValue('adults.0.checkAll', isChecked);
  };

  if (desiredCGUS.length > 1) {
    return (
      <div className="mt-10 text-12" data-testid="multiCGUBlock-checkAll">
        <Capsule
          header="none"
          additionalStyle={{
            content: {
              padding: '1.25rem',
            },
          }}
        >
          <FormikInput
            additionnalClasses="text-18 mb-10"
            className="text-18 text-black"
            label={i18n.booking.participants.agreeAll}
            name="adults.0.checkAll"
            onChange={checkAll}
            type="checkbox"
          />
          <div className="mb-10">{i18n.booking.participants.personalDataIntroduction}</div>
          {/* CGUs */}
          {desiredCGUS.map((desiredCGUItem, index) => {
            return (
              <CGUItem
                key={`CGUItem-${index + 1}`}
                cguItem={desiredCGUItem as CGUItemSingle}
                i18n={i18n}
              />
            );
          })}

          <div>
            {i18n.booking.participants.disagreePersonalDataCollection}
            <DynamicLink target="_blank" url={i18n.booking.participants.personalDataCollectURL}>
              {i18n.booking.participants.personalDataCollectLink}
            </DynamicLink>
          </div>
        </Capsule>
      </div>
    );
  }

  // Existant (EX : 1 GCU Russia CNIL)
  if (desiredCGUS.length === 1) {
    if (desiredCGUS[0].required) {
      const uniqueCguItem = desiredCGUS[0];
      const cleanUrl = uniqueCguItem?.url?.startsWith('http')
        ? uniqueCguItem.url
        : `https://${uniqueCguItem.url}`;
      return (
        <div className="mt-10" data-testid="multiCGUBlock-cguItem">
          <Title className="mb-10" theme="title-brand-n2">
            {uniqueCguItem.label}
          </Title>
          <Capsule
            header="none"
            className="flex"
            additionalStyle={{
              content: {
                padding: '1.25rem',
              },
            }}
          >
            <div className="flex">
              <FormikInput
                isRequired
                className="flex-none p-20"
                name={`adults.0.optinsCGU.${uniqueCguItem.id}.optin`}
                type="checkbox"
                onChange={() => {
                  formik.setFieldValue(`adults.0.optinsCGU.${uniqueCguItem.id}`, {
                    channel: uniqueCguItem.id,
                    optin: !formik.values.adults[0].optinsCGU?.[uniqueCguItem.id]?.optin,
                    optin_partners: false,
                  });
                }}
                validate={getLegalAgreementValidator()}
                isFlex={false}
              />
              <div className="mb-10 whitespace-pre-wrap text-black">
                <span>{uniqueCguItem.description}</span>
                <br />
                {cleanUrl && uniqueCguItem?.urlLabel && (
                  <a href={cleanUrl} rel="noopener noreferrer" target="_blank">
                    <span className="underline">{uniqueCguItem.urlLabel}</span>
                  </a>
                )}
              </div>
            </div>
          </Capsule>
        </div>
      );
    }

    return (
      <div className="mt-10" data-testid="multiCGUBlock-cguItem">
        <div>{desiredCGUS[0].label}</div>
        <TextSeeMore
          text={`${desiredCGUS[0].description} \n\n(2) ${t('booking.participants.subscription.condition')}`}
          theme="text-sans-n2"
          seeMoreLabel={i18n.booking.participants.legalMessage.seeMoreLabel}
        />
      </div>
    );
  }

  return null;
};
